/**
 * DeviceStatus — renders the read-only shadow status categories:
 * spec §9.3 device, runtime, projector, sensors, remote — plus the
 * gateway-emitted gateway and modem categories.
 *
 * This is "what is the device reporting right now" — distinct from
 * Settings (configurable categories the operator can change). No edit
 * affordances; nothing in this section ever gets a Save button.
 *
 * Each category card pulls fields directly from shadow.reported.<cat>.
 * Unknown / missing keys render "—".
 */
import { RebootDeviceControl } from './RebootDeviceDialog';
import { useDeviceUpdateJobs, useGgHealth, usePublishedReleases } from '../api/hooks';
import { ggHealthPill } from './ggHealth';
import {
  fmtSysState,
  fmtErrState,
  fmtDemoMode,
  fmtProjSource,
} from './ucEnums';
import { NO_RELEASE_LABEL, describeReleaseSource } from '../lib/releaseId';
import { supersededRelease } from '../lib/osVersion';
import {
  factoryContentLabel,
  PRE_VERSIONING_FACTORY_CONTENT,
  type FactoryContent,
} from './contentSync';

type Shadow = Record<string, Record<string, unknown> | undefined>;

function dash(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function fmtUptime(s: number | null): string {
  if (s === null) return '—';
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round(s / 3600)} h`;
  const days = Math.floor(s / 86400);
  const hours = Math.round((s % 86400) / 3600);
  return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
}

function fmtTilt(tenthsDeg: number | null): string {
  if (tenthsDeg === null) return '—';
  if (tenthsDeg === 32767) return 'no accelerometer';
  return `${(tenthsDeg / 10).toFixed(1)}°`;
}

function fmtBattery(pct: number | null): string {
  if (pct === null) return '—';
  if (pct === 255) return 'not connected';
  return `${pct} %`;
}

// DE-135 — NTC thermistors report -99 as the "no measurement" sentinel
// (open circuit / sensor not connected). Showing it as -99 °C is misleading.
function fmtNtc(v: number | null): string {
  if (v === null || v === -99) return '—';
  return `${v} °C`;
}

// DE-135 — projector temperature sensors report -100 as the no-measurement
// sentinel (model doesn't expose that sensor, or sensor is offline).
function fmtProjTemp(v: number | null): string {
  if (v === null || v === -100) return '—';
  return `${v}`;
}

function Row({ label, value, mono }: { label: string; value: string | number | undefined | null; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-3 text-xs py-0.5">
      <span className="text-muted w-32 shrink-0">{label}</span>
      <span className={mono ? 'font-mono' : 'tabular-nums'}>{value === undefined || value === null || value === '' ? '—' : value}</span>
    </div>
  );
}

function DeviceCard({ device }: { device: Record<string, unknown> | undefined }) {
  return (
    <section className="card">
      <h3 className="font-medium text-sm mb-2">Device</h3>
      {!device ? (
        <p className="text-xs text-muted">uC has not reported this category yet.</p>
      ) : (
        <div>
          <Row label="fw_ver" value={dash(device.fw_ver)} mono />
          <Row label="fw_build" value={dash(device.fw_build)} mono />
          <Row label="hw_ver" value={dash(device.hw_ver)} mono />
          <Row label="serial" value={dash(device.serial)} mono />
          <Row label="boot_reason" value={dash(device.boot_reason)} />
        </div>
      )}
    </section>
  );
}

function RuntimeCard({ runtime }: { runtime: Record<string, unknown> | undefined }) {
  return (
    <section className="card">
      <h3 className="font-medium text-sm mb-2">Runtime</h3>
      {!runtime ? (
        <p className="text-xs text-muted">uC has not reported this category yet.</p>
      ) : (
        <div>
          <Row label="sys_state" value={fmtSysState(num(runtime.sys_state))} />
          <Row label="err_state" value={fmtErrState(num(runtime.err_state))} />
          <Row label="demo_mode" value={fmtDemoMode(num(runtime.demo_mode))} />
          <Row label="uptime" value={fmtUptime(num(runtime.uptime_s))} />
        </div>
      )}
    </section>
  );
}

function ProjectorCard({ projector }: { projector: Record<string, unknown> | undefined }) {
  const temps = (projector?.temps ?? null) as Record<string, unknown> | null;
  return (
    <section className="card">
      <h3 className="font-medium text-sm mb-2">Projector</h3>
      {!projector ? (
        <p className="text-xs text-muted">uC has not reported this category yet.</p>
      ) : (
        <div>
          <Row label="mfr" value={dash(projector.mfr)} />
          <Row label="model" value={dash(projector.model)} />
          <Row label="serial" value={dash(projector.serial)} mono />
          <Row label="source" value={fmtProjSource(num(projector.source))} />
          <Row label="lamp_hrs" value={num(projector.lamp_hrs) !== null ? `${projector.lamp_hrs} h` : '—'} />
          <Row label="op_hrs" value={num(projector.op_hrs) !== null ? `${projector.op_hrs} h` : '—'} />
          {temps && (() => {
            // Only render cells for sensors that actually returned a
            // measurement. -100 is the projector's "no measurement"
            // sentinel (model doesn't expose this sensor or it's
            // offline) — `fmtProjTemp` already treats it as `—`, but
            // showing five dashed boxes adds noise without information.
            const available = ['intake', 'exhaust', 'filter', 'light1', 'light2']
              .filter((k) => {
                const v = num(temps[k]);
                return v !== null && v !== -100;
              });
            if (available.length === 0) return null;
            return (
              <div className="mt-2">
                <div className="text-muted text-[10px] mb-1">temperatures (°C)</div>
                <div
                  className="grid gap-1 text-xs tabular-nums"
                  style={{ gridTemplateColumns: `repeat(${available.length}, minmax(0, 1fr))` }}
                >
                  {available.map((k) => (
                    <div key={k} className="bg-soft rounded px-1 py-0.5 text-center">
                      <div className="text-[10px] text-muted">{k}</div>
                      <div>{fmtProjTemp(num(temps[k]))}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </section>
  );
}

function SensorsCard({ sensors }: { sensors: Record<string, unknown> | undefined }) {
  return (
    <section className="card">
      <h3 className="font-medium text-sm mb-2">Sensors</h3>
      {!sensors ? (
        <p className="text-xs text-muted">uC has not reported this category yet.</p>
      ) : (
        <div>
          <Row label="temp_int" value={num(sensors.temp_int) !== null ? `${sensors.temp_int} °C` : '—'} />
          <Row label="humidity" value={num(sensors.humidity) !== null ? `${sensors.humidity} %` : '—'} />
          <Row label="temp_ntc1" value={fmtNtc(num(sensors.temp_ntc1))} />
          <Row label="temp_ntc2" value={fmtNtc(num(sensors.temp_ntc2))} />
          <Row label="tilt_angle" value={fmtTilt(num(sensors.tilt_angle))} />
          <Row label="keystone_v" value={num(sensors.keystone_v) !== null ? `${sensors.keystone_v}` : '—'} />
        </div>
      )}
    </section>
  );
}

function RemoteCard({ remote }: { remote: Record<string, unknown> | undefined }) {
  return (
    <section className="card">
      <h3 className="font-medium text-sm mb-2">Remote</h3>
      {!remote ? (
        <p className="text-xs text-muted">uC has not reported this category yet.</p>
      ) : (
        <div>
          <Row label="connected" value={remote.connected === true ? 'yes' : remote.connected === false ? 'no' : '—'} />
          <Row label="battery" value={fmtBattery(num(remote.battery))} />
        </div>
      )}
    </section>
  );
}

// DE-133 — surfaces shadow.gateway: the components currently INSTALLED on the
// device (vs. group's TARGETED deployment shown on OpsDeviceGroups), plus the
// Mender artifact, GG nucleus, last boot, and hostname. Source is the
// telemetry-publisher snapshot per CLAUDE.md shadow layout.
function GatewayCard({
  gateway,
  thing,
}: {
  gateway: Record<string, unknown> | undefined;
  thing?: string;
}) {
  // DE-279 — control-plane health pill. The shadow list below is
  // self-reported (frozen after a reflash wipes the components); the pill
  // reads what the nucleus reports to AWS, so it stays honest exactly when
  // the shadow is stale. On a query error the pill renders nothing.
  const ggHealth = useGgHealth(thing);
  const jobs = useDeviceUpdateJobs(thing);
  const deployInFlight = (jobs.data?.active ?? []).some((j) => j.kind === 'greengrass');
  const pill = ggHealthPill(ggHealth.data, { deployInFlight });
  const components = (gateway?.gg_components ?? null) as Record<string, string> | null;
  // Artome components first (artome.*), then alphabetical (aws.*, etc.).
  const sortedComponents = components
    ? Object.entries(components).sort(([a], [b]) => {
        const ap = a.startsWith('artome.') ? 0 : 1;
        const bp = b.startsWith('artome.') ? 0 : 1;
        if (ap !== bp) return ap - bp;
        return a.localeCompare(b);
      })
    : [];

  const fmtBoot = (iso: unknown): string => {
    if (typeof iso !== 'string' || !iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  };

  return (
    <section className="card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-sm">Gateway</h3>
        {pill && (
          <span
            className={{ ok: 'pill-accent', warn: 'pill-warn', muted: 'pill-muted' }[pill.tone]}
            title={pill.title}
          >
            {pill.label}
          </span>
        )}
      </div>
      {!gateway ? (
        <p className="text-xs text-muted">Greengrass has not reported this category yet.</p>
      ) : (
        <div>
          <Row label="hostname" value={dash(gateway.hostname)} mono />
          <Row label="thing" value={dash(gateway.thing)} mono />
          <Row label="mender_artifact" value={dash(gateway.mender_artifact)} mono />
          {/* DE-171 app version axis — running (active) vs rootfs floor (baseline);
              they differ while a /data app override is active or rolled back. */}
          <Row label="app_active" value={dash(gateway.app_active_version)} mono />
          <Row label="app_baseline" value={dash(gateway.app_baseline_version)} mono />
          <Row label="gg_nucleus" value={dash(gateway.gg_nucleus_version)} mono />
          <Row label="last_boot" value={fmtBoot(gateway.last_boot_iso)} />
          {sortedComponents.length > 0 && (
            <div className="mt-2">
              <div className="text-muted text-[10px] mb-1">
                installed components ({sortedComponents.length}) — self-reported (shadow)
              </div>
              <table className="w-full text-[11px]">
                <tbody>
                  {sortedComponents.map(([name, version]) => (
                    <tr key={name} className="border-b border-border last:border-0">
                      <td className="py-0.5 pr-2 font-mono truncate">{name}</td>
                      <td className="py-0.5 tabular-nums text-right font-mono">{version}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// DE-187 — radio-metric ratings. Thresholds per ticket: RSRP ≥ −95 dBm and
// SINR ≥ 10 dB are "good"; SINR < 0 dB means the link is barely usable.
type Rating = 'good' | 'fair' | 'poor';

function rateMetric(v: number, good: number, fair: number): Rating {
  if (v >= good) return 'good';
  if (v >= fair) return 'fair';
  return 'poor';
}

function RatingPill({ rating }: { rating: Rating }) {
  const cls = rating === 'good' ? 'pill-accent' : rating === 'fair' ? 'pill-muted' : 'pill-warn';
  return <span className={cls}>{rating}</span>;
}

// A metric row with unit and optional good/fair/poor rating pill.
function SignalRow({ label, v, unit, good, fair }: {
  label: string; v: number | null; unit: string; good?: number; fair?: number;
}) {
  return (
    <div className="flex items-baseline gap-3 text-xs py-0.5">
      <span className="text-muted w-32 shrink-0">{label}</span>
      <span className="tabular-nums">{v === null ? '—' : `${v} ${unit}`}</span>
      {v !== null && good !== undefined && fair !== undefined && (
        <RatingPill rating={rateMetric(v, good, fair)} />
      )}
    </div>
  );
}

// Per-Rx-chain power (QMI modems only). A failed antenna/cable path shows one
// chain ~15–20 dB below the other — flag the imbalance.
function RxChainsRow({ chains }: { chains: Array<{ chain?: number; power_dbm?: number }> }) {
  // RX powers are always negative dBm; a non-negative value is a TX-power
  // parsing artifact from old firmware (fixed device-side in aws.79).
  const tuned = chains.filter((c) => typeof c.power_dbm === 'number' && c.power_dbm < 0);
  if (tuned.length === 0) return null;
  const powers = tuned.map((c) => c.power_dbm as number);
  const imbalance = powers.length >= 2 && Math.max(...powers) - Math.min(...powers) > 15;
  return (
    <div className="flex items-baseline gap-3 text-xs py-0.5">
      <span className="text-muted w-32 shrink-0">RX chains</span>
      <span className="tabular-nums">
        {tuned.map((c) => `RX${c.chain ?? '?'} ${c.power_dbm} dBm`).join(' · ')}
      </span>
      {imbalance && <span className="pill-warn">imbalance</span>}
    </div>
  );
}

// Onomondo ICCIDs end in a Luhn check digit; the 9 digits before it are the
// human-readable SIM identity. Bold those 9 so they stand out; leave the
// prefix and the trailing check digit in normal weight.
// e.g. 89457300000044114075 → 8945730000 + 004411407(bold) + 5
function IccidValue({ iccid }: { iccid: string }) {
  if (iccid.length < 10) return <span className="font-mono">{iccid}</span>;
  return (
    <span className="font-mono">
      {iccid.slice(0, -10)}
      <strong className="font-semibold">{iccid.slice(-10, -1)}</strong>
      {iccid.slice(-1)}
    </span>
  );
}

// Gateway-emitted modem category (artomeui-modem-manager → telemetry-publisher).
// Basic cellular + operator info plus the SIM ICCID (the SIM-card identity used
// to match the device to its line in the carrier dashboard).
function ModemCard({ modem }: { modem: Record<string, unknown> | undefined }) {
  const iccid = modem?.iccid ? String(modem.iccid) : '';
  return (
    <section className="card">
      <h3 className="font-medium text-sm mb-2">Modem</h3>
      {!modem ? (
        <p className="text-xs text-muted">No cellular modem reported.</p>
      ) : (
        <div>
          <Row label="state" value={dash(modem.state)} />
          <Row label="operator" value={dash(modem.operator)} />
          <Row label="technology" value={dash(modem.technology)} />
          <Row label="signal" value={num(modem.signal) !== null ? `${modem.signal} %` : '—'} />
          {/* DE-187 — actionable radio metrics (null on old firmware → dashes).
              SINR is the throughput predictor; RSRP measures antenna/coverage. */}
          <SignalRow label="RSRP" v={num(modem.rsrp)} unit="dBm" good={-95} fair={-110} />
          <SignalRow label="RSRQ" v={num(modem.rsrq)} unit="dB" good={-10} fair={-15} />
          <SignalRow label="SINR" v={num(modem.snr)} unit="dB" good={10} fair={0} />
          <SignalRow label="RSSI" v={num(modem.rssi)} unit="dBm" />
          {Array.isArray(modem.rx_chains) && <RxChainsRow chains={modem.rx_chains} />}
          <Row label="model" value={dash(modem.model)} mono />
          <Row label="interface" value={dash(modem.interface)} mono />
          {/* ICCID needs a custom value node (bolded significant digits), so
              render the row inline rather than via <Row>. */}
          <div className="flex items-baseline gap-3 text-xs py-0.5">
            <span className="text-muted w-32 shrink-0">SIM ICCID</span>
            {iccid ? <IccidValue iccid={iccid} /> : <span>—</span>}
          </div>
        </div>
      )}
    </section>
  );
}

// DE-189 — per-interface network state from shadow.host.interfaces
// (artomeui-sysinfo → telemetry-publisher). The management LAN (end0/eth0,
// DHCP, venue-dependent) is the one ops asks about: connected, and on what IP?
const IFACE_ORDER = ['end0', 'eth0', 'wlan0', 'wwan0', 'wwan-ecm0', 'uc0'];
const IFACE_NOTES: Record<string, string> = {
  end0: 'management LAN',
  eth0: 'management LAN',
  wwan0: 'cellular',
  'wwan-ecm0': 'cellular',
  uc0: 'uC link',
  wlan0: 'wifi (scan only)',
};

function NetworkCard({ host }: { host: Record<string, unknown> | undefined }) {
  const interfaces = (host?.interfaces ?? null) as
    | Record<string, { operstate?: string; up?: boolean; carrier?: boolean;
        ip?: string | null; mac?: string | null; dhcp_lease?: boolean }>
    | null;
  const names = interfaces
    ? Object.keys(interfaces).sort((a, b) => {
        const ai = IFACE_ORDER.indexOf(a); const bi = IFACE_ORDER.indexOf(b);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi) || a.localeCompare(b);
      })
    : [];
  return (
    <section className="card">
      <h3 className="font-medium text-sm mb-2">Network</h3>
      {!interfaces || names.length === 0 ? (
        <p className="text-xs text-muted">No interface data reported (needs firmware ≥ aws.78).</p>
      ) : (
        <div>
          {names.map((name) => {
            const i = interfaces[name] ?? {};
            const isMgmt = name === 'end0' || name === 'eth0';
            const link = i.carrier ? 'good' : i.up ? 'poor' : null;
            return (
              <div key={name} className="flex items-baseline gap-3 text-xs py-0.5">
                <span className="text-muted w-32 shrink-0 font-mono">
                  {name}
                  {IFACE_NOTES[name] && (
                    <span className="block text-[10px]">{IFACE_NOTES[name]}</span>
                  )}
                </span>
                <span className="font-mono">{i.ip ?? '—'}</span>
                {link === 'good' && <span className="pill-accent">link</span>}
                {link === 'poor' && <span className="pill-warn">no link</span>}
                {link === null && <span className="pill-muted">down</span>}
                {isMgmt && i.carrier && (
                  i.dhcp_lease
                    ? <span className="pill-accent">lease ✓</span>
                    : <span className="pill-warn">no lease</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * DE-223 — the PUBLIC release this device reports (shadow `release`, written by
 * telemetry only when the release's part versions match what is actually
 * running). Three distinct states, and telling them apart is the point:
 *
 *   an ID + managed  — assigned by a Portal release and verified applied
 *   an ID + factory  — shipped with the installed image, no Portal release since
 *   no ID + pending  — a release is staged but its parts are not applied yet
 *                      … or SUPERSEDED (DE-318): the device runs a newer OS than
 *                      the release names, so its parts can never match again and
 *                      "pending" never clears. Told apart by comparing the running
 *                      OS with the release's OS part for this board.
 *   nothing at all   — never received a published release (the normal case today)
 *
 * "Resolves to" restates the part versions the release names, read from the
 * categories that own them, so the release ID can be checked against reality
 * without cross-referencing three other cards.
 */
function ReleaseCard({ shadow }: { shadow: Shadow }) {
  const release = shadow.release;
  const id = typeof release?.id === 'string' ? release.id : null;
  const source = typeof release?.source === 'string' ? release.source : null;
  const assignedAt = typeof release?.assigned_at === 'string' ? release.assigned_at : null;
  const pendingId = typeof release?.pending_id === 'string' ? release.pending_id : null;

  const gateway = shadow.gateway;
  // DE-318 — has the device moved past the release it is still assigned?
  const published = usePublishedReleases();
  const superseded = supersededRelease(
    release,
    published.data?.releases,
    typeof gateway?.device_type === 'string' ? gateway.device_type : null,
    typeof gateway?.mender_artifact === 'string' ? gateway.mender_artifact : null,
  );
  const content = shadow.content as Record<string, unknown> | undefined;
  const appliedProfile = content?.appliedProfile as Record<string, unknown> | null | undefined;
  // Manufacturing provenance, NOT the applied profile: which factory content set
  // this unit was flashed with, and which .sdimg produced it. Reported from
  // /data/artomeui/factory-content.json, which nothing on the device rewrites —
  // so it survives content deploys that replace the applied-profile record.
  const factory = content?.factoryContent as FactoryContent | null | undefined;
  const factoryLabel = factoryContentLabel(factory);

  const fmtWhen = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  };

  return (
    <section className="card">
      <h3 className="font-medium text-sm mb-2">Release</h3>
      {superseded ? (
        <div>
          <Row label="release" value={`${superseded.releaseId} (superseded)`} mono />
          <p className="text-xs text-muted mt-2">
            The device has moved on to {superseded.runningOs}, newer than
            the {superseded.namedOs} this release names,
            so it will never report this release. Nothing is waiting to apply; assigning a newer
            release clears this.
          </p>
        </div>
      ) : id ? (
        <div>
          <Row label="release" value={id} mono />
          <Row label="source" value={describeReleaseSource(source) ?? dash(source)} />
          {assignedAt && <Row label="assigned" value={fmtWhen(assignedAt)} />}
          <p className="text-[11px] text-muted mt-2 mb-1">Resolves to</p>
          <Row label="OS" value={dash(gateway?.mender_artifact)} mono />
          <Row label="app" value={dash(gateway?.app_active_version)} mono />
          <Row
            label="content"
            value={
              appliedProfile
                ? `${dash(appliedProfile.name)} v${dash(appliedProfile.version)}`
                : '—'
            }
          />
          <Row label="uC fw" value={dash(shadow.device?.fw_ver)} mono />
        </div>
      ) : pendingId ? (
        <div>
          <Row label="release" value={`${pendingId} (staged)`} mono />
          <p className="text-xs text-muted mt-2">
            {describeReleaseSource('pending')}. The device keeps reporting no release until the
            staged parts are applied — usually at the next power-on.
          </p>
        </div>
      ) : (
        <p className="text-xs text-muted">
          {NO_RELEASE_LABEL}. This device has not received a Portal release carrying a public
          release ID, so there is nothing to report — expected for internally flashed units.
        </p>
      )}

      {/* Outside the release conditional on purpose: a unit that never took a
          Portal release still has manufacturing provenance, and that is precisely
          the unit you want it for. */}
      <div className="mt-3 pt-3 border-t border-[var(--border)]">
        <p className="text-[11px] text-muted mb-1">Manufactured with</p>
        <Row
          label="factory content"
          value={
            factoryLabel.inferred
              ? `${factoryLabel.version} (assumed)`
              : factoryLabel.version
          }
          mono
        />
        {factory?.image && <Row label="flashed image" value={String(factory.image)} mono />}
        {factoryLabel.inferred && (
          <p className="text-[11px] text-muted mt-1">
            This unit's image predates factory-content reporting.{' '}
            {PRE_VERSIONING_FACTORY_CONTENT} is the only content set ever manufactured, so that is
            what it holds — but the device is not saying so itself.
          </p>
        )}
      </div>
    </section>
  );
}

export function DeviceStatus({ shadow, thing }: { shadow: Shadow; thing?: string }) {
  const allMissing =
    !shadow?.device && !shadow?.runtime && !shadow?.projector && !shadow?.sensors && !shadow?.remote && !shadow?.gateway && !shadow?.modem;

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold tracking-tight">Status</h2>
        <div className="flex items-baseline gap-3">
          {/* DE-277 — the one action on an otherwise read-only card. */}
          {thing && <RebootDeviceControl thing={thing} />}
          <span className="text-xs text-muted">Read-only — reported by the device.</span>
        </div>
      </div>
      {allMissing ? (
        <div className="card">
          <p className="text-sm text-muted">
            uC not reporting; status categories unavailable. (When uc-bridge is alive on this
            device, the cards below populate automatically.)
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <DeviceCard device={shadow.device} />
          <RuntimeCard runtime={shadow.runtime} />
          <ProjectorCard projector={shadow.projector} />
          <SensorsCard sensors={shadow.sensors} />
          <RemoteCard remote={shadow.remote} />
          <ModemCard modem={shadow.modem} />
          <NetworkCard host={shadow.host} />
          <GatewayCard gateway={shadow.gateway} thing={thing} />
          <ReleaseCard shadow={shadow} />
        </div>
      )}
    </section>
  );
}
